import React, {FC, useState, useRef, useEffect} from "react";

import {Table, Space, Modal, Dropdown, Menu, message, Layout, Form, Row, Col, Input, Button, Divider, Spin, FormInstance, Alert} from 'antd';

import {DownOutlined, RedoOutlined, UpOutlined} from '@ant-design/icons';

import {Pageable, Pagination} from './../../../../utils/data/Type';
import TableUtils from './../../../../utils/ui/table/TableUtils';
import { OutsideTowerCraneLog, OutsideTowerCraneLogQto } from './../../type/OutsideTowerCraneLogType';
import OutsideTowerCraneLogApi from "../../api/OutsideTowerCraneLogApi";
import OutsideTowerCraneLogAddForm from "./OutsideTowerCraneLogAddForm";
import OutsideTowerCraneLogDetail from "./OutsideTowerCraneLogDetail";
import { ColumnsType, ColumnType } from "antd/lib/table";
import TableSizeSetting from "../../../../components/antx/table/TableSizeSetting";
import { SizeType } from "antd/lib/config-provider/SizeContext";
import TableColumnSetting from "../../../../components/antx/table/TableColumnSetting";
import ProForm, { ProFormInstance, ProFormText } from "@ant-design/pro-form";

const getTableScrollY = (size:SizeType, pagination:any, tableHeight:number)=>{

    let scrollY = tableHeight;

    switch(size){
        case 'small':
            scrollY = scrollY-39;
            if(pagination){
                scrollY = scrollY-56;
            }
            return scrollY;
        case 'middle':
            scrollY = scrollY-47;
            if(pagination){
                scrollY = scrollY-56;
            }
            return scrollY;
        default:
            scrollY = scrollY-55;
            if(pagination){
                scrollY = scrollY-64;
            }
            return scrollY;
    }
}

const OutsideTowerCraneLogPage:React.FC = ()=>{
    const container = useRef<HTMLDivElement>(null);
    const queryFormRef = useRef<ProFormInstance<OutsideTowerCraneLogQto>>(null);

    const [addFormVisible, setAddFormVisible] = useState<boolean>(false); //新建表单可见状态
    const [detailVisible, setDetailVisible] = useState<boolean>(false); //明细可见状态
    const [queryFormExpand, setQueryFormExpand] = useState<boolean>(false); //查询表单展开状态
    const [currentOutsideTowerCraneLog, setCurrentOutsideTowerCraneLog] = useState<OutsideTowerCraneLog>({}); //当前记录
    const [tableSize, setTableSize] = useState<SizeType>('middle'); //表格尺寸
    const [columns, setColumns] = useState<ColumnsType<OutsideTowerCraneLog>>([]); //表格列
    const [tableHeight, setTableHeight] = useState<number>(0); //表格高度
    const [queryParams, setQueryParams] = useState<any>({}); //查询参数
    const [pageData, setPageData] = useState<Pagination>(); //分页查询服务端返回数据
    const [spinning, setSpinning] = useState<boolean>(false); //加载状态
    const [selectedRows, setSelectedRows] = useState<OutsideTowerCraneLog[]>([]);

    const defaultColumns:ColumnsType<OutsideTowerCraneLog> = [
        {
            key: "rowNo",
            title: "序号",
            dataIndex: "rowNo",
            width: 70,
            fixed: 'left',
        },
        {
            key: "outKey",
            title: "外部记录ID",
            dataIndex: "outKey",
            width: 100,
            ellipsis: true,
        },
        {
            key: "deviceSn",
            title: "设备序列号",
            dataIndex: "deviceSn",
            width: 100,
            ellipsis: true,
        },
        {
            key: "deviceTime",
            title: "设备时间",
            dataIndex: "deviceTime",
            width: 100,
            ellipsis: true,
        },
        {
            key: "weight",
            title: "当前吊重(千克)",
            dataIndex: "weight",
            width: 100,
            ellipsis: true,
        },
        {
            key: "maxWeight",
            title: "安全吊重(千克)",
            dataIndex: "maxWeight",
            width: 100,
            ellipsis: true,
        },
        {
            key: "height",
            title: "当前高度(米)",
            dataIndex: "height",
            width: 100,
            ellipsis: true,
        },
        {
            key: "rotary",
            title: "当前旋转角度(度)",
            dataIndex: "rotary",
            width: 100,
            ellipsis: true,
        },
        {
            key: "radius",
            title: "当前幅度(米)",
            dataIndex: "radius",
            width: 100,
            ellipsis: true,
        },
        {
            key: "momentOfForcePercent",
            title: "力矩百分比",
            dataIndex: "momentOfForcePercent",
            width: 100,
            ellipsis: true,
        },
        {
            key: "ropeMagnification",
            title: "吊绳倍率",
            dataIndex: "ropeMagnification",
            width: 100,
            ellipsis: true,
        },
        {
            key: "slantAngel",
            title: "倾斜角度",
            dataIndex: "slantAngel",
            width: 100,
            ellipsis: true,
        },
        {
            key: "slantDirection",
            title: "倾斜方向",
            dataIndex: "slantDirection",
            width: 100,
            ellipsis: true,
        },
        {
            key: "windSpeed",
            title: "当前风速(米/秒)",
            dataIndex: "windSpeed",
            width: 100,
            ellipsis: true,
        },
        {
            key: "warningStatus",
            title: "系统预警状态编码",
            dataIndex: "warningStatus",
            width: 100,
            ellipsis: true,
        },
        {
            key: "alarmStatus",
            title: "系统报警状态编码",
            dataIndex: "alarmStatus",
            width: 100,
            ellipsis: true,
        },
        {
            key: "irregularitiesStatus",
            title: "系统违规操作状态编码",
            dataIndex: "irregularitiesStatus",
            width: 100,
            ellipsis: true,
        },
        {
            key: "firstRunDate",
            title: "首次运行时间",
            dataIndex: "firstRunDate",
            width: 100,
            ellipsis: true,
        },
        {
            key: "lastRunDate",
            title: "末次运行时间",
            dataIndex: "lastRunDate",
            width: 100,
            ellipsis: true,
        },
        {
            key: "openTime",
            title: "本次开机时间",
            dataIndex: "openTime",
            width: 100,
            ellipsis: true,
        },
        {
            key: "lastUpdateTime",
            title: "最近更新时间",
            dataIndex: "lastUpdateTime",
            width: 100,
            ellipsis: true,
        },
        {
            key: "driverId",
            title: "驾驶员ID",
            dataIndex: "driverId",
            width: 100,
            ellipsis: true,
        },
        {
            key: "driverName",
            title: "驾驶员姓名",
            dataIndex: "driverName",
            width: 100,
            ellipsis: true,
        },
        {
            key: "towerCraneLogId",
            title: "塔吊黑匣子记录ID",
            dataIndex: "towerCraneLogId",
            width: 100,
            ellipsis: true,
        },
        {
            title: '操作',
            width: 120,
            dataIndex: 'actions',
            fixed: 'right',
            render: (val, record) =>{
                return [
                    <a key='view' onClick={()=>{
                        setCurrentOutsideTowerCraneLog(record);
                        setDetailVisible(true);
                        }}>
                        查看
                    </a>,
                    <Divider type="vertical"  key="split1"/>,
                    rowExtraButtons({item:record}),
                ];
            }
        },
    ];

    const reloadData = (params:object|undefined)=>{

        setSpinning(true);
        const newQueryParams = {
            ...(queryFormRef.current?.getFieldsFormatValue&&queryFormRef.current?.getFieldsFormatValue()||{}),
            ...(params||{}),
            deletedFlag: false,
        };
        setQueryParams(newQueryParams);

        OutsideTowerCraneLogApi.findOutsideTowerCraneLogPage(newQueryParams).then(pageInfo=>{
            pageInfo.list.forEach((i:any,idx:number)=>{
                i.rowNo = idx + 1 + (pageInfo.pageNum-1)*pageInfo.pageSize;
            });
            setPageData(pageInfo);
        }).finally(()=>{
            setSpinning(false);
        });
    }

    const batchDelete = async()=>{
        if(selectedRows.length<1){
            message.error("请先选择数据");
            return;
        }

        Modal.confirm({
            title: "批量删除",
            content: "确定删除所选塔吊原始记录吗？",
            okText: "确定",
            cancelText: "取消",
            onOk: async () => {
                for(let i=0; i<selectedRows.length; i++){
                    const row = selectedRows[i];

                    await OutsideTowerCraneLogApi.deleteOutsideTowerCraneLog(row);
                }

                message.success("数据删除成功！");
                reloadData({});
            }
        });
    }

    //表格大小变化事件
    useEffect(()=>{

        setColumns(defaultColumns);
        reloadData({});

        const interval = setInterval(()=>{
            if(container.current?.offsetHeight!=tableHeight){
                console.log(tableHeight);
                setTableHeight(container.current?.offsetHeight||0);
            }
        }, 200);

        return ()=>{
            clearInterval(interval);
        };
    }, []);

    const deleteOutsideTowerCraneLog = (outsideTowerCraneLog:OutsideTowerCraneLog)=>{
        Modal.confirm({
            title: "删除塔吊原始记录确认",
            content: "确定删除该塔吊原始记录吗？",
            okText: "确定",
            cancelText: "取消",
            onOk: async () => {
                await OutsideTowerCraneLogApi.deleteOutsideTowerCraneLog(outsideTowerCraneLog);
            }
        });
    }

    const rowExtraButtons : React.FC<{
        item: OutsideTowerCraneLog
    }> = ({item}) => {
        return (
            <Dropdown
                key="row-extra-btns"
                overlay={
                    <Menu
                        onClick={({key})=>{
                            if(key==='delete') {
                                deleteOutsideTowerCraneLog(item);
                            }
                        }}
                    >
                        <Menu.Item key="delete">
                            删除
                        </Menu.Item>
                    </Menu>
                }
            >
                <a key='extraBtn'>
                    更多 <DownOutlined />
                </a>
            </Dropdown>
        );
    };

    const formFieldSpan={ xs: 24, sm: 12, md: 8, lg: 6, xl:6, xxl:6 };

    const queryFormFields = [
        <div key='qf1' style={{display:'inline-block', height:40, width:260}}>
            <ProFormText
                label="企业名称"
                name="name"
            />
        </div>,
    ];

    const getQueryFields = ()=>{
        let endIdx = queryFormExpand&&queryFormFields.length>2?queryFormFields.length:2;

        const fields = [];

        for(let i=0; i<endIdx; i++){
            fields.push(queryFormFields[i])
        }

        return fields;
    }

    return (
        <div style={{width:'100%', height:'100%', display: "flex", flexDirection: 'column'}}>
            <div style={{backgroundColor:'#ffffff', padding:"8px 16px 0px 16px", marginBottom:12}}>
                <ProForm
                    formRef={queryFormRef}
                    submitter={false}
                    layout={'horizontal'}
                    initialValues={{

                    }}
                >
                    <Space align="start" wrap>
                        { getQueryFields()}

                        <Button
                            type="primary"
                            onClick={()=>{
                                reloadData({});
                            }}
                        >
                            查询
                        </Button>

                        <Button
                            type="primary"
                            danger
                            onClick={()=>{
                                queryFormRef.current?.resetFields();
                            }}
                        >
                            重置
                        </Button>

                        {
                            queryFormFields.length>2 &&
                            <Button
                                type="link"
                                onClick={()=>{
                                    setQueryFormExpand(!queryFormExpand);
                                }}
                            >
                                {queryFormExpand?'收起':'展开'}{queryFormExpand?<UpOutlined />:<DownOutlined />}
                            </Button>
                        }
                    </Space>
                </ProForm>
            </div>

            <div style={{backgroundColor:'#ffffff', height:56, lineHeight:'56px', paddingLeft:16, paddingRight:16}}>
                <div style={{fontWeight:'bold', display:'inline-block'}}>
                    塔吊原始记录列表
                </div>
                <div style={{display: 'inline-block', float: 'right'}}>
                    <Space size={16}>
                        {
                            selectedRows.length>0 &&
                            <Button type="link">
                                已选择 <strong>{selectedRows.length}</strong> 条
                            </Button>
                        }

                        <Button
                            type="primary"
                            onClick={()=>{
                                setAddFormVisible(true);
                            }}
                        >
                            新建
                        </Button>
                        <Dropdown.Button
                            overlay={
                               <Menu
                                    onClick={({key})=>{
                                        if(key==='batchDelete'){
                                            batchDelete();
                                        }
                                    }}
                                    items={[{
                                        key: 'batchDelete',
                                        label: '批量删除',
                                    }]}
                               />
                            }
                        >
                            批量操作
                        </Dropdown.Button>
                        <RedoOutlined
                            style={{fontSize:16, cursor:'pointer'}}
                            onClick={()=>{
                                reloadData({});
                            }}
                        />
                        <TableSizeSetting
                            onChange={(val)=>{
                                setTableSize(val)
                            }}
                        />
                        <TableColumnSetting
                            columns={columns}
                            onChange={(value)=>{
                                setColumns(value)
                            }}
                        />
                    </Space>
                </div>
            </div>
            <div style={{flex:1, position:'relative'}}>
                <div
                    ref={container}
                    style={{width: '100%', height:'100%', position:'absolute'}}
                >
                    <Spin spinning={spinning}>
                        <Table
                            dataSource={pageData?.list}
                            pagination={{
                                current: queryParams?.page||1,
                                pageSize: queryParams?.pageSize||10,
                                total: pageData?.total,
                                pageSizeOptions: [10, 20, 50, 100],
                                showQuickJumper: true,
                                showSizeChanger: true,
                                onChange:(page, pageSize)=>{
                                    reloadData({
                                        page: page,
                                        pageSize: pageSize,
                                    });
                                },
                                onShowSizeChange:(page, pageSize)=>{
                                    reloadData({
                                        page: page,
                                        pageSize: pageSize,
                                    });

                                }
                            }}
                            size={tableSize}
                            rowKey="id"
                            columns={columns}
                            scroll={{x: TableUtils.getTableScrollX(columns), y: getTableScrollY(tableSize, true, tableHeight), scrollToFirstRowOnChange:true}}
                            rowSelection={{
                                selections: [Table.SELECTION_ALL, Table.SELECTION_INVERT],
                                defaultSelectedRowKeys: [],
                                onChange:(selectedRowKeys, selectedRows)=>{
                                    setSelectedRows(selectedRows);
                                }
                            }}
                        />
                    </Spin>
                </div>
            </div>


            {
                addFormVisible &&
                <OutsideTowerCraneLogAddForm
                    visible={true}
                    onSuccess={()=>{
                        setAddFormVisible(false);
                        reloadData({});
                    }}
                    onFail={()=>{
                        setAddFormVisible(false);
                    }}
                />
            }

            {
                detailVisible &&
                <OutsideTowerCraneLogDetail
                    outsideTowerCraneLog={currentOutsideTowerCraneLog}
                    visible={true}
                    onSuccess={()=>{
                        setDetailVisible(false);
                        reloadData({});
                    }}
                    onFail={()=>{
                        setDetailVisible(false);
                    }}
                />
            }
        </div>
    );
};

export default OutsideTowerCraneLogPage;